import type { ExpressAvailabilityStatus } from "@dransay/api";

const CACHE_PREFIX = "express-availability:";

/**
 * One cached express availability outcome. Holds the reasoned status from the
 * fulfillment `/check` -- never a bare boolean -- so a consumer can tell
 * "outside the delivery area" apart from "outside the ordering window".
 */
export interface CachedExpressAvailability {
	status: ExpressAvailabilityStatus;
	errorCode?: string;
}

function buildAreaKey(postalCode: string): string {
	return `${CACHE_PREFIX}area:${postalCode}`;
}

function buildVendorKey(postalCode: string, vendorId: number): string {
	return `${CACHE_PREFIX}vendor:${postalCode}:${vendorId}`;
}

/**
 * Marker key for "the PLP availability modal was already offered for this PLZ".
 *
 * It deliberately shares the cache prefix and the PLZ key position so
 * `invalidateExpressAvailabilityCacheExcept` discards it on an address change,
 * re-arming the modal for the new area without a second invalidation path.
 */
function buildModalSeenKey(postalCode: string): string {
	return `${CACHE_PREFIX}modal-seen:${postalCode}`;
}

/**
 * Marker key for "the header promo card was dismissed for this PLZ".
 *
 * It deliberately shares the cache prefix and the PLZ key position so
 * `invalidateExpressAvailabilityCacheExcept` discards it on an address change,
 * re-arming the card for the new area without a second invalidation path. It
 * lives in its own key namespace so dismissing the card and the modal being
 * offered never suppress one another.
 */
function buildHeaderCardDismissedKey(postalCode: string): string {
	return `${CACHE_PREFIX}header-card-dismissed:${postalCode}`;
}

const VALID_STATUSES: readonly ExpressAvailabilityStatus[] = [
	"available",
	"unavailable_time",
	"unavailable_location",
	"unknown",
];

/**
 * An entry written by an older build, or hand-edited in DevTools, can hold any
 * shape. Validating here keeps an unknown payload from being handed to a
 * consumer as if it were a real verdict.
 */
function isCachedExpressAvailability(
	value: unknown,
): value is CachedExpressAvailability {
	if (typeof value !== "object" || value === null || Array.isArray(value)) {
		return false;
	}
	const candidate = value as Record<string, unknown>;
	const hasValidStatus = VALID_STATUSES.includes(
		candidate.status as ExpressAvailabilityStatus,
	);
	const hasValidErrorCode =
		candidate.errorCode === undefined ||
		typeof candidate.errorCode === "string";
	return hasValidStatus && hasValidErrorCode;
}

/**
 * Resolving `sessionStorage` can itself throw: it is a `Window` accessor, and a
 * browser configured to block site data answers the read with a SecurityError
 * instead of `undefined`. A bare `typeof sessionStorage` guard would throw at
 * the guard itself, so every access goes through here.
 */
function getSessionStorage(): Storage | null {
	try {
		return typeof sessionStorage === "undefined" ? null : sessionStorage;
	} catch {
		return null;
	}
}

function readEntry(key: string): CachedExpressAvailability | null {
	const storage = getSessionStorage();
	if (!storage) {
		return null;
	}
	try {
		const raw = storage.getItem(key);
		if (!raw) {
			return null;
		}
		const parsed: unknown = JSON.parse(raw);
		return isCachedExpressAvailability(parsed) ? parsed : null;
	} catch {
		return null;
	}
}

function writeEntry(key: string, value: CachedExpressAvailability): void {
	const storage = getSessionStorage();
	if (!storage) {
		return;
	}
	try {
		storage.setItem(key, JSON.stringify(value));
	} catch {
		// Ignore quota errors — the cache is a best-effort optimization.
	}
}

export function getCachedAreaAvailability(
	postalCode: string,
): CachedExpressAvailability | null {
	return readEntry(buildAreaKey(postalCode));
}

export function setCachedAreaAvailability(
	postalCode: string,
	value: CachedExpressAvailability,
): void {
	writeEntry(buildAreaKey(postalCode), value);
}

export function getCachedVendorAvailability(
	postalCode: string,
	vendorId: number,
): CachedExpressAvailability | null {
	return readEntry(buildVendorKey(postalCode, vendorId));
}

export function setCachedVendorAvailability(
	postalCode: string,
	vendorId: number,
	value: CachedExpressAvailability,
): void {
	writeEntry(buildVendorKey(postalCode, vendorId), value);
}

export function hasSeenAvailabilityModal(postalCode: string): boolean {
	const storage = getSessionStorage();
	if (!storage) {
		return false;
	}
	try {
		return storage.getItem(buildModalSeenKey(postalCode)) !== null;
	} catch {
		return false;
	}
}

export function markAvailabilityModalSeen(postalCode: string): void {
	const storage = getSessionStorage();
	if (!storage) {
		return;
	}
	try {
		storage.setItem(buildModalSeenKey(postalCode), "1");
	} catch {
		// Ignore quota errors — at worst the modal is offered once more.
	}
}

export function isHeaderCardDismissed(postalCode: string): boolean {
	const storage = getSessionStorage();
	if (!storage) {
		return false;
	}
	try {
		return storage.getItem(buildHeaderCardDismissedKey(postalCode)) !== null;
	} catch {
		return false;
	}
}

export function markHeaderCardDismissed(postalCode: string): void {
	const storage = getSessionStorage();
	if (!storage) {
		return;
	}
	try {
		storage.setItem(buildHeaderCardDismissedKey(postalCode), "1");
	} catch {
		// Ignore quota errors — at worst the card is shown once more.
	}
}

/**
 * Drops every cached outcome that does not belong to `postalCode`.
 *
 * Phrased as "keep only the current PLZ" rather than "drop this old PLZ" so a
 * caller never has to remember the address the customer just replaced, and so
 * storage stays bounded across a long browsing session.
 */
export function invalidateExpressAvailabilityCacheExcept(
	postalCode: string,
): void {
	const storage = getSessionStorage();
	if (!storage) {
		return;
	}
	try {
		const staleKeys: string[] = [];
		for (let index = 0; index < storage.length; index += 1) {
			const key = storage.key(index);
			if (!key?.startsWith(CACHE_PREFIX)) {
				continue;
			}
			const [, keyPostalCode] = key.slice(CACHE_PREFIX.length).split(":");
			if (keyPostalCode !== postalCode) {
				staleKeys.push(key);
			}
		}
		for (const key of staleKeys) {
			storage.removeItem(key);
		}
	} catch {
		// Ignore storage failures — the cache is a best-effort optimization.
	}
}
